<template>
  <AccordionCard
    v-show="!cardSelected || (isEditing && isCollapsed)"
    :hideChevrons="true"
    :isCollapsed="isCollapsed">
    <template slot="title">{{ cardTitle }}</template>
    <template slot="buttons">
      <a
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2--off text-danger"
        :href="webLink"
        target="_blank"
        v-if="!isCollapsed">
        <font-awesome-icon :icon="['fas', 'globe']" class="mr-1" /> Open
      </a>
      <button
        class="btn btn-outline-brand btn-outline-danger btn-elevate btn-circle btn-icon btn-sm"
        :disabled="isLoading || isEditing"
        @click="() => {emitSelectAccordionCard(cardName);resetSelectedSections();}"
        v-if="!isCollapsed">
        <font-awesome-icon :icon="['fas', 'pen']" />
      </button>
      <button
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2"
        :disabled="isSaving"
        @click="cancel"
        v-if="isCollapsed">
        <font-awesome-icon :icon="['fas', 'times']" class="mr-1" /> Cancel
      </button>
      <button
        class="btn btn-sm btn-wide btn-primary"
        :disabled="isSaving"
        @click="save"
        v-if="isCollapsed">
        <font-awesome-icon :icon="['fas', 'check']" class="mr-1" v-if="!isSaving" />
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-1" v-if="isSaving" /> Done
      </button>
    </template>

    <ul class="nav nav-tabs nav-tabs-line nav-tabs-bold nav-tabs-line-3x nav-tabs-line-info mb-4" role="tablist"
      v-if="selectedTab !== tabs.PREVIEW_TAB">
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_IMAGES_TAB }"
          @click.prevent="selectTab(tabs.SELECT_IMAGES_TAB)">&nbsp;&nbsp;Select images&nbsp;&nbsp;</a>
      </li>
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_SECTIONS_TAB }"
          @click.prevent="selectTab(tabs.SELECT_SECTIONS_TAB)">&nbsp;&nbsp;Sections&nbsp;&nbsp;</a>
      </li>
    </ul>

    <div class="container-fluid" v-show="selectedTab === tabs.SELECT_SECTIONS_TAB">
      <p class="my-2 mb-4">Please select the section to be included into the quote for the PDF and Weblink</p>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.cover.active"
            checked
            disabled
          > Cover <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
          type="checkbox"
          v-model="selectedSections.letter.active"
          :disabled="isTrafalgar"
          > Letter <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input type="checkbox" v-model="selectedSections.itinerary.active"
          checked
          disabled
          > Itinerary <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.inclusions.active"
            :disabled="isTrafalgar"
          > Inclusions <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.map.active"
            :disabled="isTrafalgar"
          > Map <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.pricing.active"
            :disabled="isTrafalgar"
          > Pricing <span></span>
        </label>
      </div>
      <div v-if="isTrafalgar" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.airContract.active"
            :disabled="isDA"
          > Air Contract <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.bookMyGroup.active"
            :disabled="isTrafalgar"
          > Book My Group <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.contactUs.active"
            :disabled="isTrafalgar"
          > Contact us <span></span>
        </label>
      </div>
      <div v-if="isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.shipDetails.active"
          > Ship Details <span></span>
        </label>
      </div>
    </div>

    <div class="container-fluid" v-show="selectedTab === tabs.SELECT_IMAGES_TAB">
      <div class="row mb-5">
        <div class="form-group col-md-2">
          <label class="labelTitle">Select your template</label>
        </div>
        <div class="col-md-10">
          <ImagePicker
            :isDuplicated="false"
            :imageUrl="getTemplateImageUrl"
            :isEditing="true"
            :total="totalImages"
            :images="images"
            :countries="countries"
            :isSearching="isImagePickerSearching"
            :defaultParams="defaultImagePickerParams"
            :limitMinOfSelectedImages="limitMinOfSelectedImagesTemplate"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImagesTemplate"
            :labelSearch="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            :labelButton="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            @onSearch="onImagePickerSearch"
            @onSelectImage="onImagePickerSelectTemplateImages"
            style="width: 150px;height: auto;" />
            <div class="text-danger my-3" v-if="hasErrorsTemplate">This field is required and It needs to select {{ limitMaxOfSelectedImagesTemplate }} image(s).</div>
        </div>
      </div>
    </div>
  </AccordionCard>
</template>

<script>
import { mapGetters } from 'vuex'
import NProgress from 'nprogress'
import cloneDeep from 'lodash.clonedeep'

import { buildNewAssets, getValidatedImages, getValidatedExportSection } from '@/helpers'
import {OPPORTUNITY_EXPORT_SECTIONS_IDS, OPPORTUNITY__BRANDS} from '@/constants'


import AccordionCard from '@/components/_shared/accordion/AccordionCard.vue'
import ImagePicker from '@/components/_shared/image-picker/ImagePicker.vue'

export default {
  name: 'TabShareWebLink',
  data () {
    const PREVIEW_TAB = 'preview-tab'
    const SELECT_IMAGES_TAB = 'select-images-tab'
    const LINE_BREAKS_NUMBER_TAB = 'line-breaks-number-tab'
    const SELECT_SECTIONS_TAB = 'select-sections-tab'

    const IDS_BRANDS_SELECT_SECTIONS = [13, 14, OPPORTUNITY__BRANDS.TRAFALGAR_RIVER]

    return {
      IDS_BRANDS_SELECT_SECTIONS,
      webLinkUrl: process.env.VUE_APP_WEB_LINK_URL,
      isLoading: false,
      templateImagesSelected: null,
      images: [],
      totalImages: 0,
      isImagePickerSearching: false,
      limitMinOfSelectedImagesTemplate: 3,
      limitMaxOfSelectedImagesTemplate: 3,
      isSaving: false,
      hasErrorsTemplate: false,
      selectedTab: SELECT_IMAGES_TAB,
      tabs: {
        PREVIEW_TAB,
        SELECT_IMAGES_TAB,
        LINE_BREAKS_NUMBER_TAB,
        SELECT_SECTIONS_TAB
      },
      selectedSections: this.getValidatedSelectedSections()
    }
  },
  components: {
    AccordionCard,
    ImagePicker
  },
  computed: {
    ...mapGetters({
      'countries': 'location/countries',
      'opportunityData': 'opportunity/opportunity',
    }),
    defaultImagePickerParams () {
      return {
        limit: 21,
        offset: 0,
        w: 150,
        h: 150,
        country: '',
        city: ''
      }
    },
    getTemplateImageUrl () {
      if (
        this.templateImagesSelected &&
        Array.isArray(this.templateImagesSelected) &&
        this.templateImagesSelected.length > 1) {
        return this.templateImagesSelected[0].url_image
      }
      if (this.data &&
          Array.isArray(this.data[this.imageField]) &&
          this.data[this.imageField].length === this.limitMaxOfSelectedImagesTemplate) {
        return this.data[this.imageField][0].url_image
      }
      return ''
    },
    webLink () {
      const uuid = this.$route.params.privateId
      const wlu = this.webLinkUrl
      return `${wlu}/${this.cardName}/${uuid}`
    },
    isDA() {
      return this.opportunityData.opportunity_details.id_brand === 13;
    },
    isTrafalgar() {
      return this.opportunityData.opportunity_details.id_brand === 14;
    },
    isTrafalgarRiver() {
      return this.opportunityData.opportunity_details.id_brand === OPPORTUNITY__BRANDS.TRAFALGAR_RIVER;
    },
  },
  props: {
    cardName: {
      type: String,
      required: true
    },
    cardTitle: {
      type: String,
      required: true
    },
    pdfContentType: {
      type: String,
      required: true
    },
    webContentType: {
      type: String,
      required: true
    },
    imageField: {
      type: String,
      required: true
    },
    cardSelected: {
      type: String,
      required: true
    },
    isCollapsed: {
      type: Boolean,
      required: true
    },
    isEditing: {
      type: Boolean,
      required: true
    },
    isCompletedQuote: {
      type: Boolean,
      default: false
    },
    data: {
      type: [Array, Object],
      required: false
    },
    templateSetting: {
      type: [Array, Object],
      required: false
    }
  },
  methods: {
    resetSelectedSections () {
      this.selectedSections = this.getValidatedSelectedSections()
    },
    getValidatedSelectedSections() {
      return {
        dates: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.DATES),
        cover: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.COVER),
        letter: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.LETTER),
        itinerary: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.ITINERARY),
        inclusions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.INCLUSIONS),
        map: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.MAP),
        pricing: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.PRICING),
        airContract: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.AIR_CONTRACT),
        termsConditions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.TERMS_CONDITIONS),
        bookMyGroup: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.BOOK_MY_GROUP),
        contactUs: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.CONTACT_US),
        shipDetails: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.SHIP_DETAILS),
      }
    },
    validateExportSection(exportSectionId = 0) {
      return getValidatedExportSection(this.templateSetting, exportSectionId, this.webContentType)
    },
    selectTab (tabName) {
      this.selectedTab = tabName
    },
    emitSelectAccordionCard (cardName) {
      this.$emit('onSelect', cardName)
    },
    onImagePickerSearch (params) {
      if (params.country === '' && params.city === '') {
        this.isImagePickerSearching = false
        this.totalImages = 0
        this.images = []
        return false
      }
      this.isImagePickerSearching = true
      this.$store.dispatch('asset/getAssets', params)
        .then(response => {
          this.images = response.asset // Array images
          this.totalImages = response.total
          this.isImagePickerSearching = false
        })
        .catch(error => {
          console.log(error)
          this.isImagePickerSearching = false
          this.totalImages = 0
          this.images = []
        })
    },
    onImagePickerSelectTemplateImages (imagesSelected) {
      this.templateImagesSelected = imagesSelected
    },
    cancel () {
      this.templateImagesSelected = null
      this.resetSelectedSections()
      this.emitSelectAccordionCard('')
    },
    isValidBrandForChoosingSections () {
      const idBrand = this.opportunityData.opportunity_details.id_brand
      const brandFound = this.IDS_BRANDS_SELECT_SECTIONS.includes(idBrand)
      return brandFound;
    },
    getUpdatedSelectedSectionsByExportType (currentContents = []) {
      return Object.values(this.selectedSections).map(section => {
        const currentContent = currentContents.find(content => content.id_content === section.id_content)
        return {
          ...section,
          id: currentContent !== undefined ? currentContent.id : null,
          status: section.active ? 1 : -1
        }
      }).filter(section => !(section.status === -1 && [undefined, null].includes(section.id)));
    },
    save () {
      if (this.selectedTab === this.tabs.SELECT_IMAGES_TAB) {
        this.saveSelectImagesTab()
      } else if (this.selectedTab === this.tabs.SELECT_SECTIONS_TAB) {
        this.saveSelectSectionsTab()
      }
    },
    saveSelectSectionsTab() {
      if (!this.isValidBrandForChoosingSections()) return this.$notification.warning('Feature not available for this brand.', 5000);

      const pastOpportunitySettings = this.templateSetting || [];

      const quotePdfExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === this.pdfContentType
      );
      const weblinkExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === this.webContentType
      );

      const adaptedTemplateSections = pastOpportunitySettings.map(opportunitySetting => {
        if (opportunitySetting.type === this.pdfContentType && quotePdfExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        if (opportunitySetting.type === this.webContentType && weblinkExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        return opportunitySetting;
      });

      if (!quotePdfExists) {
        adaptedTemplateSections.push({
          id: null,
          type: this.pdfContentType,
          contents: this.getUpdatedSelectedSectionsByExportType([])
        });
      }
      if (!weblinkExists) {
        adaptedTemplateSections.push({
          id: null,
          type: this.webContentType,
          contents: this.getUpdatedSelectedSectionsByExportType([])
        });
      }

      let newOpportunityData = cloneDeep(this.opportunityData)
      newOpportunityData.opportunity_settings = adaptedTemplateSections

      this.$store.commit('opportunity/TEMP_SAVE_OPPORTUNITY_SETTINGS', adaptedTemplateSections )

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to open the web link with its new settings.', 5000)
            this.emitSelectAccordionCard('')
            this.templateImagesSelected = null
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    },
    saveSelectImagesTab () {
      if (!this.templateImagesSelected) {
        if (this.data &&
          Array.isArray(this.data[this.imageField]) &&
          this.data[this.imageField].length === this.limitMaxOfSelectedImagesTemplate) {
          this.templateImagesSelected = this.data[this.imageField]
        }
      }

      const imagesTemplate = buildNewAssets(this.templateImagesSelected);
      const templateImageSize = this.templateImagesSelected ? this.templateImagesSelected.length : 0;

      const backgroundImagesValidated = getValidatedImages(this.opportunityData.images[this.imageField], imagesTemplate, templateImageSize)

      const items = [
        {
          images: backgroundImagesValidated,
          type: this.imageField,
        }
      ]

      if (backgroundImagesValidated && backgroundImagesValidated.length > 0) {
        this.$store.commit('opportunity/TEMP_SAVE_WEB_LINK', items)
        this.$store.commit('opportunity/TEMP_EXPORT_BACKGROUND_FROM_OPPORTUNITY_TO_CONSUMER_SECTION', items)
      }

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to open the web link with its new settings.', 5000)
            this.emitSelectAccordionCard('')
            this.templateImagesSelected = null
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    }
  }
}
</script>
